import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useNoIndex from "@/lib/useNoIndex";
import {
  Sparkles, Scissors, HeartHandshake, Camera, Clock, Settings2,
  Download, MapPin, AlertCircle, Loader2,
} from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TYPE_META = {
  setup:    { icon: Settings2,     accent: "text-neutral-600" },
  hair:     { icon: Scissors,      accent: "text-primary" },
  makeup:   { icon: Sparkles,      accent: "text-primary" },
  combo:    { icon: Sparkles,      accent: "text-primary" },
  ready:    { icon: Camera,        accent: "text-neutral-700" },
  ceremony: { icon: HeartHandshake, accent: "text-primary" },
  travel:   { icon: Clock,         accent: "text-neutral-500" },
  other:    { icon: Clock,         accent: "text-neutral-500" },
};

export default function PublicTimeline() {
  useNoIndex();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/public/timeline/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body?.detail || "Link non valido");
        }
        return r.json();
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const couple = data
    ? `${data.client.name}${data.client.partner_name ? " & " + data.client.partner_name : ""}`
    : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center p-6">
        <div className="bg-white border border-border rounded-sm p-10 max-w-md text-center" data-testid="public-timeline-error">
          <AlertCircle className="w-10 h-10 text-neutral-400 mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif-heading text-3xl">Link non valido</h1>
          <p className="text-sm text-neutral-600 mt-2">
            {error || "Il link condiviso non è valido o è stato revocato."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="public-timeline-page">
      <div className="max-w-3xl mx-auto py-10 px-5">
        {/* Header card */}
        <div className="bg-white border border-border rounded-sm p-8 sm:p-10">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="label-uppercase text-primary">Timeline · sola lettura</span>
            <a
              href={`${API}/public/timeline/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="public-timeline-pdf"
            >
              <Button variant="outline" size="sm" className="rounded-sm">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> PDF
              </Button>
            </a>
          </div>
          <h1 className="font-serif-heading text-4xl sm:text-5xl mt-3 leading-tight" data-testid="public-timeline-couple">
            {couple || "Timeline matrimonio"}
          </h1>
          <div className="mt-3 text-sm text-neutral-600 flex flex-wrap gap-x-5 gap-y-1.5">
            {data.client.wedding_date_formatted && (
              <span className="flex items-center gap-1.5"><HeartHandshake className="w-4 h-4 text-primary" strokeWidth={1.5} /> {data.client.wedding_date_formatted}</span>
            )}
            {data.client.ceremony_time && (
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" strokeWidth={1.5} /> Cerimonia {data.client.ceremony_time}</span>
            )}
            {data.client.venue && (
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" strokeWidth={1.5} /> {data.client.venue}</span>
            )}
          </div>
          {data.owner_name && (
            <div className="text-xs text-neutral-400 mt-3">a cura di {data.owner_name}</div>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-white border border-border rounded-sm mt-5 p-8 sm:p-10">
          <div className="label-uppercase mb-6">Programma della giornata</div>

          {data.timeline.length === 0 ? (
            <div className="text-sm text-neutral-500 text-center py-10" data-testid="public-timeline-empty">
              Nessun blocco timeline ancora pubblicato.
            </div>
          ) : (
            <ol className="relative border-l-2 border-primary/30 ml-3 space-y-5" data-testid="public-timeline-list">
              {data.timeline.map((b, idx) => {
                const meta = TYPE_META[b.type] || TYPE_META.other;
                const Icon = meta.icon;
                return (
                  <li key={b.id || idx} className="ml-6" data-testid={`public-timeline-block-${idx}`}>
                    <span className="absolute -left-[11px] flex items-center justify-center w-5 h-5 bg-white border-2 border-primary rounded-full">
                      <Icon className={`w-3 h-3 ${meta.accent}`} strokeWidth={2} />
                    </span>
                    <div className="bg-secondary/30 border border-border rounded-sm p-4">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="font-serif-heading text-2xl tabular-nums">{b.time || "—"}</span>
                        <span className="font-medium text-neutral-900">{b.title || "—"}</span>
                        <span className="label-uppercase text-neutral-400 ml-auto">{b.type_label}</span>
                      </div>
                      <div className="text-xs text-neutral-500 mt-1.5 flex gap-3">
                        {b.duration > 0 && <span>{b.duration} min</span>}
                      </div>
                      {b.notes && <div className="text-sm text-neutral-600 mt-2">{b.notes}</div>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="text-center text-xs text-neutral-400 mt-6">
          Powered by <span className="font-serif-heading text-neutral-600">AI Bridal Manager</span>™
        </div>
      </div>
    </div>
  );
}
